import { computeConditionQuantitySlots } from "../config/conditionCalcTypes";
import { isMetric, fmtQty } from "./units";

// The takeoff LEGEND: part of the DRAWING (not a floating panel) — drawn inside the canvas SVG in drawing space, so it
// scales and pans with the sheet and is in any snapshot or printout of the markup. It lists the conditions on the
// drawing with Quantity 1, 2 and / or 3 (each switched on separately) and follows the canvas's own visibility: a
// condition on a hidden Takeoff Layer (or hidden with the eye) is not in it. Its settings live WITH the drawing
// (TakeoffDrawingSheets.LegendJson, db/38) so everyone who opens the sheet — and every printout — sees the same one.
// Text size is in PAPER POINTS, converted through the sheet's scale (feet per paper inch), so a 12 pt legend prints at
// 12 pt on the sheet like any drawing text. Every number is the canvas's own quantity function: the legend agrees with
// the labels, the Conditions dock and Σ Totals.

export const LEGEND_FONT_SIZES = Object.freeze([6, 7, 8, 9, 10, 12, 14, 16, 18, 24]);   // paper points
export const DEFAULT_LEGEND = Object.freeze({ on: false, q1: true, q2: false, q3: false, code: true, fontPt: 10, pos: null });

const num = (v) => (Number.isFinite(+v) && v !== null && v !== "" ? +v : null);

/** Whatever was stored (or nothing) -> a complete, valid settings object. `pos` is world feet, or null = the default corner. */
export function normalizeLegend(raw) {
  const s = raw && typeof raw === "object" ? raw : {};
  const fontPt = LEGEND_FONT_SIZES.includes(+s.fontPt) ? +s.fontPt : DEFAULT_LEGEND.fontPt;
  const x = s.pos ? num(s.pos.x) : null, y = s.pos ? num(s.pos.y) : null;
  return { on: !!s.on, q1: s.q1 === undefined ? DEFAULT_LEGEND.q1 : !!s.q1, q2: !!s.q2, q3: !!s.q3, code: s.code === undefined ? DEFAULT_LEGEND.code : !!s.code, fontPt, pos: x !== null && y !== null ? { x, y } : null };
}
/** The drawing's stored LegendJson string -> settings. */
export function parseLegend(json) {
  try { return normalizeLegend(typeof json === "string" && json ? JSON.parse(json) : json); } catch { return normalizeLegend(null); }
}
export const legendToJson = (legend) => JSON.stringify(normalizeLegend(legend));

/** Which of the three quantity slots (0, 1, 2) are switched on. */
export const enabledSlots = (legend) => [legend.q1, legend.q2, legend.q3].map((on, i) => (on ? i : -1)).filter(i => i >= 0);

/** Text size in WORLD FEET for a paper-point size on a sheet drawn at `ftPerInch` (72 pt = 1 paper inch). */
export const legendFontFeet = (fontPt, ftPerInch) => (fontPt / 72) * (ftPerInch > 0 ? ftPerInch : 8);

/**
 * One legend entry per condition type: { key, label, code, color, style, count, slots: [ {q,u}|null ×3 ] } — each slot
 * summed over every run of that type. `isVisible(row)` is the canvas's layer / eye rule; back-outs and untyped rows are left out.
 */
export function legendEntries(rows, { keyOf, isVisible = () => true, showCode = true } = {}) {
  const map = new Map();
  for (const c of rows || []) {
    if (c.MeasureRole === "deduct") continue;
    const key = keyOf(c);
    if (!key || key === "unassigned" || !isVisible(c)) continue;
    if (!map.has(key)) map.set(key, { key, label: c.Name || c.Code || key, code: showCode && c.Code && c.Name && c.Code !== c.Name ? c.Code : null, color: c.Color || null, style: c.Style || "linear", count: 0, slots: [null, null, null] });
    const g = map.get(key);
    g.count += 1;
    computeConditionQuantitySlots(c).forEach((s, i) => {
      if (!s || !Number.isFinite(s.q)) return;
      g.slots[i] = g.slots[i] ? { q: g.slots[i].q + s.q, u: g.slots[i].u || s.u } : { q: s.q, u: s.u };
    });
  }
  return [...map.values()].sort((a, b) => String(a.code || a.label).localeCompare(String(b.code || b.label), undefined, { numeric: true, sensitivity: "base" }));
}

const fmtQ = (q) => Math.round(q).toLocaleString(); // whole numbers, as everywhere (utils/quantityRounding.js)
/** "200 SF" (or the metric equivalent), "—" for an empty slot. */
export const legendQtyText = (slot, units) => (slot ? (isMetric(units) ? fmtQty({ q: slot.q, u: slot.u }, units) : `${fmtQ(slot.q)} ${slot.u}`) : "—");

/**
 * Lay the legend out in world feet for font size `fs` (feet). `measure(text, { bold, mono })` returns a width in em
 * (utils/textMetrics.js; injectable so the layout is testable). Returns the box and each row's / column's position.
 */
export function legendLayout(entries, slotIdx, fs, measure, units = "imperial") {
  const pad = fs * 0.7, lh = fs * 1.6, sw = fs * 0.95, gap = fs * 1.1;
  const titleH = lh * 1.15, hasHead = slotIdx.length > 0, headH = hasHead ? lh * 0.95 : 0;
  const labelOf = (e) => (e.code ? `${e.code}  ${e.label}` : e.label);
  const labelW = Math.max(0, ...entries.map(e => measure(labelOf(e), { bold: false }) * fs));
  const colW = slotIdx.map(i => Math.max(measure(`Qty ${i + 1}`, { bold: true }) * fs * 0.8, ...entries.map(e => measure(legendQtyText(e.slots[i], units), { mono: true }) * fs)));
  const titleW = measure("Legend", { bold: true }) * fs * 1.05;
  const bodyW = sw + gap * 0.6 + labelW + colW.reduce((a, w) => a + gap + w, 0);
  const width = pad * 2 + Math.max(bodyW, titleW + pad * 4);
  const rows = entries.map((e, r) => ({ entry: e, label: labelOf(e), y: pad + titleH + headH + r * lh }));
  const height = pad + titleH + headH + Math.max(entries.length, 1) * lh + pad;
  const colRight = [];
  let x = pad + sw + gap * 0.6 + labelW;
  for (const w of colW) { x += gap + w; colRight.push(x); }
  return { width, height, pad, lh, sw, gap, titleH, headH, rows, colRight, labelX: pad + sw + gap * 0.6, fs };
}
